/**
 * ProfileDialog -- Commander profile & fleet overview.
 *
 * Two views:
 *   1. Main view  -- profile summary + fleet list
 *   2. Ship detail -- stats, components, crew for a single ship
 *
 * Data is fetched via REST from /api/profile/me, /api/profile/me/ships,
 * and /api/profile/me/ships/:id.
 */

import Phaser from 'phaser';
import { EventBus } from '../../utils/EventBus';
import {
  getResponsiveDialogSize,
  getResponsiveFontSize,
  createScrollableContainer,
} from '../../utils/ResponsiveHelper';
import { I18n } from '../../i18n/I18nService';

// ---- Colour & layout constants (matches ShipStoreDialog) ----

const DESKTOP_WIDTH = 560;
const DESKTOP_HEIGHT = 620;
const BG_COLOR = 0x0f1a2e;
const BORDER_COLOR = 0x3366aa;
const HEADER_COLOR = 0x1a2a4e;
const CARD_COLOR = 0x162040;
const CARD_HOVER_COLOR = 0x1e2e55;
const ACCENT_COLOR = 0x44aaff;
const SECTION_HEADER_COLOR = 0x192844;

// ---- Response interfaces (mirror server JSON) ----

interface ProfileData {
  userId: string;
  displayName: string;
  email: string | null;
  walletAddress: string | null;
  createdAt: string;
  shipCount: number;
  purchaseCount: number;
  totalSpent: number;
  alliance: { id: string; name: string } | null;
}

interface ShipSummary {
  id: string;
  unitType: string;
  customName?: string | null;
  shipVariant?: string | null;
  currentHp: number;
  maxHp: number;
  shields: number;
  maxShields: number;
  position: { q: number; r: number };
  createdAt: string;
  crew: { id: string; name: string; level: number } | null;
}

interface StationSummary {
  id: string;
  stationType: string;
  positionQ: number | null;
  positionR: number | null;
  currentHp: number;
  maxHp: number;
  currentShields: number;
  maxShields: number;
  status: string;
  stationFeePct: number;
}

interface ComponentInfo {
  id: string;
  name: string;
  category: string;
  currentTier: number;
  stats: Record<string, number> | null;
  description: string;
}

interface CrewDetail {
  id: string;
  name: string;
  level: number;
  combat_skill: number;
  piloting_skill: number;
  navigation_skill: number;
  engineering_skill: number;
  science_skill: number;
  status: string;
}

interface ShipDetail {
  ship: {
    id: string;
    unitType: string;
    customName?: string | null;
    shipVariant?: string | null;
    currentHp: number;
    maxHp: number;
    shields: number;
    maxShields: number;
    position: { q: number; r: number };
    createdAt: string;
  };
  components: ComponentInfo[];
  crew: CrewDetail | null;
}

// ---- Display-name helpers ----

function unitDisplayName(type: string): string {
  return I18n.shipName(type);
}

// ====================================================================
// ProfileDialog
// ====================================================================

export class ProfileDialog extends Phaser.GameObjects.Container {
  // Data
  private profile: ProfileData | null = null;
  private ships: ShipSummary[] = [];
  private stations: StationSummary[] = [];
  private shipDetail: ShipDetail | null = null;
  private dims: ReturnType<typeof getResponsiveDialogSize>;

  // UI containers
  private scrollInner: Phaser.GameObjects.Container | null = null;
  private scrollUpdate: (() => void) | null = null;
  private statusText: Phaser.GameObjects.Text;

  constructor(scene: Phaser.Scene) {
    const dims = getResponsiveDialogSize(DESKTOP_WIDTH, DESKTOP_HEIGHT, scene);
    super(scene, dims.x, dims.y);
    this.dims = dims;

    const { width, height } = scene.cameras.main;
    const dw = dims.width;
    const dh = dims.height;

    // --- Dark overlay ---
    const overlay = scene.add.rectangle(-dims.x, -dims.y, width, height, 0x000000, 0.7);
    overlay.setInteractive();
    overlay.on('pointerdown', () => this.close());
    this.add(overlay);

    // --- Main panel background ---
    const bg = scene.add.rectangle(0, 0, dw, dh, BG_COLOR, 1);
    bg.setOrigin(0, 0);
    bg.setInteractive();
    this.add(bg);

    // --- Border ---
    const border = scene.add.graphics();
    border.lineStyle(2, BORDER_COLOR, 1);
    border.strokeRect(0, 0, dw, dh);
    this.add(border);

    // --- Header bar ---
    const headerBg = scene.add.rectangle(0, 0, dw, 50, HEADER_COLOR, 1);
    headerBg.setOrigin(0, 0);
    this.add(headerBg);

    const title = scene.add.text(dw / 2, 25, I18n.t('dialog.commander_profile'), {
      fontSize: `${getResponsiveFontSize(20, scene)}px`,
      color: '#44aaff',
      fontFamily: 'Orbitron, Arial',
      fontStyle: 'bold',
    });
    title.setOrigin(0.5);
    this.add(title);

    // --- Close button ---
    this.add(this.createCloseButton(dw - 35, 25));

    // --- Scrollable content area ---
    const listTop = 55;
    const listBottom = 25;
    const listHeight = dh - listTop - listBottom;
    const { container, updateScrollBounds } = createScrollableContainer(scene, this, {
      x: 0,
      y: listTop,
      width: dw,
      height: listHeight,
    });
    this.scrollInner = container;
    this.scrollUpdate = updateScrollBounds;

    // --- Status text (bottom) ---
    this.statusText = scene.add.text(dw / 2, dh - 18, '', {
      fontSize: `${getResponsiveFontSize(12, scene)}px`,
      color: '#889999',
      fontFamily: 'Share Tech Mono, monospace',
    });
    this.statusText.setOrigin(0.5);
    this.add(this.statusText);

    // --- Depth & add to scene ---
    this.setDepth(2000);
    scene.add.existing(this);

    // --- Fetch data ---
    this.loadProfile();
  }

  // ------------------------------------------------------------------ helpers

  private getAuthHeaders(): Record<string, string> {
    const token = localStorage.getItem('crimsonmandate_token');
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  private setStatus(msg: string): void {
    this.statusText.setText(msg);
  }

  // ------------------------------------------------------------------ close

  private createCloseButton(x: number, y: number): Phaser.GameObjects.Container {
    const button = this.scene.add.container(x, y);
    const bg = this.scene.add.rectangle(0, 0, 40, 40, 0x660000);
    bg.setStrokeStyle(2, 0xff4444);
    button.add(bg);

    const text = this.scene.add.text(0, 0, 'X', {
      fontSize: '20px',
      color: '#ffffff',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    });
    text.setOrigin(0.5);
    button.add(text);

    button.setSize(40, 40);
    button.setInteractive(
      new Phaser.Geom.Rectangle(-20, -20, 40, 40),
      Phaser.Geom.Rectangle.Contains,
    );
    button.on('pointerover', () => bg.setFillStyle(0x880000));
    button.on('pointerout', () => bg.setFillStyle(0x660000));
    button.on('pointerdown', () => this.close());

    return button;
  }

  private close(): void {
    this.destroy();
  }

  // ================================================================== Data fetching

  private async loadProfile(): Promise<void> {
    this.setStatus('Loading profile...');
    try {
      const [profileRes, shipsRes, stationsRes] = await Promise.all([
        fetch('/api/profile/me', { headers: this.getAuthHeaders() }),
        fetch('/api/profile/me/ships', { headers: this.getAuthHeaders() }),
        fetch('/api/stations/list', { headers: this.getAuthHeaders() }),
      ]);

      const profileJson = await profileRes.json();
      const shipsJson = await shipsRes.json();
      const stationsJson = await stationsRes.json();

      if (!profileJson.success) {
        this.setStatus('Failed to load profile.');
        return;
      }

      this.profile = profileJson.data as ProfileData;
      this.ships = shipsJson.success ? (shipsJson.data.ships as ShipSummary[]) : [];
      this.stations = (stationsJson.stations as StationSummary[]) || [];
      this.setStatus('');
      this.renderMainView();
    } catch (err) {
      console.error('[ProfileDialog] loadProfile error:', err);
      this.setStatus('Network error. Try again later.');
    }
  }

  private async loadShipDetail(shipId: string): Promise<void> {
    this.setStatus('Loading ship detail...');
    try {
      const res = await fetch(`/api/profile/me/ships/${shipId}`, {
        headers: this.getAuthHeaders(),
      });
      const json = await res.json();

      if (!json.success) {
        this.setStatus('Failed to load ship detail.');
        return;
      }

      this.shipDetail = json.data as ShipDetail;
      this.setStatus('');
      this.renderDetailView();
    } catch (err) {
      console.error('[ProfileDialog] loadShipDetail error:', err);
      this.setStatus('Network error. Try again later.');
    }
  }

  private async handleUnassignCrew(shipId: string): Promise<void> {
    this.setStatus('Unassigning crew...');
    try {
      const res = await fetch(`/api/profile/me/ships/${shipId}/crew`, {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
      });
      const json = await res.json();
      if (json.success) {
        this.setStatus('Crew unassigned.');
        await this.loadShipDetail(shipId);
      } else {
        this.setStatus(`Failed: ${json.error?.message || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('[ProfileDialog] unassign crew error:', err);
      this.setStatus('Network error.');
    }
  }

  // ================================================================== Main View

  private renderMainView(): void {
    if (!this.scrollInner) return;
    this.scrollInner.removeAll(true);

    if (!this.profile) return;
    const p = this.profile;
    const dw = this.dims.width;

    let yOffset = 10;

    // ---- Profile summary section ----
    const summaryBg = this.scene.add.rectangle(10, yOffset, dw - 20, 80, 0x111e33, 1);
    summaryBg.setOrigin(0, 0);
    summaryBg.setStrokeStyle(1, BORDER_COLOR, 0.4);
    this.scrollInner.add(summaryBg);

    const nameText = this.scene.add.text(20, yOffset + 12, `Commander: ${p.displayName || 'Unknown'}`, {
      fontSize: `${getResponsiveFontSize(16, this.scene)}px`,
      color: '#ffffff',
      fontFamily: 'Orbitron, Arial',
      fontStyle: 'bold',
    });
    this.scrollInner.add(nameText);

    const allianceName = p.alliance ? p.alliance.name : 'None';
    const allianceText = this.scene.add.text(20, yOffset + 36, `Alliance: ${allianceName}`, {
      fontSize: `${getResponsiveFontSize(13, this.scene)}px`,
      color: '#8899bb',
      fontFamily: 'Share Tech Mono, monospace',
    });
    this.scrollInner.add(allianceText);

    const statsStr = `Ships: ${p.shipCount}   |   Total Invested: $${p.totalSpent.toFixed(2)}`;
    const statsText = this.scene.add.text(20, yOffset + 56, statsStr, {
      fontSize: `${getResponsiveFontSize(12, this.scene)}px`,
      color: '#aabbcc',
      fontFamily: 'Share Tech Mono, monospace',
    });
    this.scrollInner.add(statsText);

    yOffset += 95;

    // ---- Fleet header ----
    const fleetHeaderBg = this.scene.add.rectangle(10, yOffset, dw - 20, 30, SECTION_HEADER_COLOR, 1);
    fleetHeaderBg.setOrigin(0, 0);
    this.scrollInner.add(fleetHeaderBg);

    const fleetHeader = this.scene.add.text(dw / 2, yOffset + 15, 'YOUR FLEET', {
      fontSize: `${getResponsiveFontSize(14, this.scene)}px`,
      color: '#44aaff',
      fontFamily: 'Orbitron, Arial',
      fontStyle: 'bold',
    });
    fleetHeader.setOrigin(0.5);
    this.scrollInner.add(fleetHeader);

    yOffset += 38;

    // ---- Column headers ----
    const colHeaders = this.scene.add.text(20, yOffset, 'Ship              HP          Crew                ', {
      fontSize: `${getResponsiveFontSize(10, this.scene)}px`,
      color: '#667799',
      fontFamily: 'Share Tech Mono, monospace',
    });
    this.scrollInner.add(colHeaders);
    yOffset += 16;

    // Separator
    const sep = this.scene.add.graphics();
    sep.lineStyle(1, BORDER_COLOR, 0.3);
    sep.lineBetween(15, yOffset, dw - 15, yOffset);
    this.scrollInner.add(sep);
    yOffset += 4;

    // ---- Ship cards ----
    if (this.ships.length === 0) {
      const emptyText = this.scene.add.text(dw / 2, yOffset + 40, 'No ships in your fleet.\nVisit the Ship Store to purchase one.', {
        fontSize: `${getResponsiveFontSize(13, this.scene)}px`,
        color: '#556677',
        fontFamily: 'Share Tech Mono, monospace',
        align: 'center',
      });
      emptyText.setOrigin(0.5, 0);
      this.scrollInner.add(emptyText);
    } else {
      const cardWidth = dw - 30;
      const cardHeight = 44;

      for (const ship of this.ships) {
        const card = this.createShipCard(15, yOffset, cardWidth, cardHeight, ship);
        this.scrollInner.add(card);
        yOffset += cardHeight + 4;
      }
    }

    // ---- Stations header ----
    yOffset += 8;
    const stationHeaderBg = this.scene.add.rectangle(10, yOffset, dw - 20, 30, SECTION_HEADER_COLOR, 1);
    stationHeaderBg.setOrigin(0, 0);
    this.scrollInner.add(stationHeaderBg);

    const stationHeader = this.scene.add.text(dw / 2, yOffset + 15, 'YOUR STATIONS', {
      fontSize: `${getResponsiveFontSize(14, this.scene)}px`,
      color: '#44aaff',
      fontFamily: 'Orbitron, Arial',
      fontStyle: 'bold',
    });
    stationHeader.setOrigin(0.5);
    this.scrollInner.add(stationHeader);
    yOffset += 38;

    if (this.stations.length === 0) {
      const emptyStations = this.scene.add.text(dw / 2, yOffset + 20, 'No stations deployed.', {
        fontSize: `${getResponsiveFontSize(13, this.scene)}px`,
        color: '#556677',
        fontFamily: 'Share Tech Mono, monospace',
        align: 'center',
      });
      emptyStations.setOrigin(0.5, 0);
      this.scrollInner.add(emptyStations);
      yOffset += 50;
    } else {
      const cardWidth = dw - 30;
      const cardHeight = 44;

      for (const station of this.stations) {
        const card = this.createStationCard(15, yOffset, cardWidth, cardHeight, station);
        this.scrollInner.add(card);
        yOffset += cardHeight + 4;
      }
    }

    this.scrollUpdate?.();
  }

  private createShipCard(
    x: number,
    y: number,
    w: number,
    h: number,
    ship: ShipSummary,
  ): Phaser.GameObjects.Container {
    const card = this.scene.add.container(x, y);

    const bg = this.scene.add.rectangle(0, 0, w, h, CARD_COLOR);
    bg.setOrigin(0, 0);
    bg.setStrokeStyle(1, 0x334466);
    card.add(bg);

    const variantLabel = ship.shipVariant
      ? ship.shipVariant.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())
      : null;
    const typeName = ship.customName || variantLabel || unitDisplayName(ship.unitType);
    const typeText = this.scene.add.text(10, h / 2 - 8, typeName, {
      fontSize: `${getResponsiveFontSize(14, this.scene)}px`,
      color: '#ccddff',
      fontFamily: 'Orbitron, Arial',
      fontStyle: 'bold',
    });
    typeText.setOrigin(0, 0.5);
    card.add(typeText);

    // HP and crew info - position proportionally
    const midX = this.dims.isMobile ? w * 0.4 : 150;

    const hpStr = `HP: ${ship.currentHp}/${ship.maxHp}`;
    const hpText = this.scene.add.text(midX, h / 2 - 8, hpStr, {
      fontSize: `${getResponsiveFontSize(11, this.scene)}px`,
      color: '#88cc88',
      fontFamily: 'Share Tech Mono, monospace',
    });
    hpText.setOrigin(0, 0.5);
    card.add(hpText);

    const crewStr = ship.crew
      ? `Crew: ${ship.crew.name} Lv.${ship.crew.level}`
      : 'Crew: None';
    const crewColor = ship.crew ? '#aabbcc' : '#556677';
    const crewText = this.scene.add.text(midX, h / 2 + 8, crewStr, {
      fontSize: `${getResponsiveFontSize(10, this.scene)}px`,
      color: crewColor,
      fontFamily: 'Share Tech Mono, monospace',
    });
    crewText.setOrigin(0, 0.5);
    card.add(crewText);

    // Shields (small)
    if (ship.maxShields > 0) {
      const shieldStr = `Shields: ${ship.shields}/${ship.maxShields}`;
      const shieldText = this.scene.add.text(10, h / 2 + 8, shieldStr, {
        fontSize: `${getResponsiveFontSize(10, this.scene)}px`,
        color: '#6688cc',
        fontFamily: 'Share Tech Mono, monospace',
      });
      shieldText.setOrigin(0, 0.5);
      card.add(shieldText);
    }

    // VIEW button
    const viewBtn = this.createSmallButton(w - 55, h / 2 - 11, 45, 22, 'VIEW', () => {
      this.loadShipDetail(ship.id);
    });
    card.add(viewBtn);

    // Click + Hover — entire card opens ship detail
    card.setSize(w, h);
    card.setInteractive(
      new Phaser.Geom.Rectangle(0, 0, w, h),
      Phaser.Geom.Rectangle.Contains,
    );
    card.on('pointerover', () => bg.setFillStyle(CARD_HOVER_COLOR));
    card.on('pointerout', () => bg.setFillStyle(CARD_COLOR));
    card.on('pointerdown', () => this.loadShipDetail(ship.id));

    return card;
  }

  private createStationCard(
    x: number,
    y: number,
    w: number,
    h: number,
    station: StationSummary,
  ): Phaser.GameObjects.Container {
    const card = this.scene.add.container(x, y);

    const bg = this.scene.add.rectangle(0, 0, w, h, CARD_COLOR);
    bg.setOrigin(0, 0);
    bg.setStrokeStyle(1, 0x334466);
    card.add(bg);

    const typeName = station.stationType.charAt(0).toUpperCase() + station.stationType.slice(1);
    const typeText = this.scene.add.text(10, h / 2 - 8, typeName, {
      fontSize: `${getResponsiveFontSize(14, this.scene)}px`,
      color: '#ffcc44',
      fontFamily: 'Orbitron, Arial',
      fontStyle: 'bold',
    });
    typeText.setOrigin(0, 0.5);
    card.add(typeText);

    const midX = this.dims.isMobile ? w * 0.4 : 150;
    const hpStr = `HP: ${station.currentHp}/${station.maxHp}`;
    const hpText = this.scene.add.text(midX, h / 2 - 8, hpStr, {
      fontSize: `${getResponsiveFontSize(11, this.scene)}px`,
      color: '#88cc88',
      fontFamily: 'Share Tech Mono, monospace',
    });
    hpText.setOrigin(0, 0.5);
    card.add(hpText);

    const posStr = station.positionQ != null
      ? `(${station.positionQ}, ${station.positionR})`
      : 'Not deployed';
    const posText = this.scene.add.text(midX, h / 2 + 8, posStr, {
      fontSize: `${getResponsiveFontSize(10, this.scene)}px`,
      color: '#8899bb',
      fontFamily: 'Share Tech Mono, monospace',
    });
    posText.setOrigin(0, 0.5);
    card.add(posText);

    const statusColor = station.status === 'active' ? '#44ff44' : '#ff4444';
    const statusText = this.scene.add.text(10, h / 2 + 8, station.status.toUpperCase(), {
      fontSize: `${getResponsiveFontSize(10, this.scene)}px`,
      color: statusColor,
      fontFamily: 'Share Tech Mono, monospace',
    });
    statusText.setOrigin(0, 0.5);
    card.add(statusText);

    // Hover effect
    card.setSize(w, h);
    card.setInteractive(
      new Phaser.Geom.Rectangle(0, 0, w, h),
      Phaser.Geom.Rectangle.Contains,
    );
    card.on('pointerover', () => bg.setFillStyle(CARD_HOVER_COLOR));
    card.on('pointerout', () => bg.setFillStyle(CARD_COLOR));

    return card;
  }

  // ================================================================== Detail View

  private renderDetailView(): void {
    if (!this.scrollInner) return;
    this.scrollInner.removeAll(true);

    if (!this.shipDetail) return;
    const { ship, components, crew } = this.shipDetail;
    const dw = this.dims.width;

    let yOffset = 8;

    // ---- Header row with BACK button ----
    const backBtn = this.createSmallButton(15, yOffset, 60, 24, 'BACK', () => {
      this.renderMainView();
    });
    this.scrollInner.add(backBtn);

    const detailTitle = this.scene.add.text(dw / 2, yOffset + 12, 'SHIP DETAIL', {
      fontSize: `${getResponsiveFontSize(16, this.scene)}px`,
      color: '#44aaff',
      fontFamily: 'Orbitron, Arial',
      fontStyle: 'bold',
    });
    detailTitle.setOrigin(0.5);
    this.scrollInner.add(detailTitle);

    yOffset += 36;

    // ---- Ship info section ----
    const infoBg = this.scene.add.rectangle(10, yOffset, dw - 20, 70, 0x111e33, 1);
    infoBg.setOrigin(0, 0);
    infoBg.setStrokeStyle(1, BORDER_COLOR, 0.4);
    this.scrollInner.add(infoBg);

    const variantLabel = ship.shipVariant
      ? ship.shipVariant.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())
      : null;
    const typeName = ship.customName || variantLabel || unitDisplayName(ship.unitType);

    const line1 = this.dims.isMobile
      ? `${typeName}  HP:${ship.currentHp}/${ship.maxHp}  Sh:${ship.shields}/${ship.maxShields}`
      : `Type: ${typeName}   |   HP: ${ship.currentHp}/${ship.maxHp}   |   Shields: ${ship.shields}/${ship.maxShields}`;
    const line1Text = this.scene.add.text(20, yOffset + 14, line1, {
      fontSize: `${getResponsiveFontSize(12, this.scene)}px`,
      color: '#ccddee',
      fontFamily: 'Share Tech Mono, monospace',
    });
    this.scrollInner.add(line1Text);

    const posStr = `(${ship.position.q}, ${ship.position.r})`;
    const createdStr = new Date(ship.createdAt).toLocaleDateString();
    const line2 = `Position: ${posStr}   |   Created: ${createdStr}`;
    const line2Text = this.scene.add.text(20, yOffset + 34, line2, {
      fontSize: `${getResponsiveFontSize(12, this.scene)}px`,
      color: '#8899bb',
      fontFamily: 'Share Tech Mono, monospace',
    });
    this.scrollInner.add(line2Text);

    const shipIdText = this.scene.add.text(20, yOffset + 52, `ID: ${ship.id}`, {
      fontSize: `${getResponsiveFontSize(10, this.scene)}px`,
      color: '#556677',
      fontFamily: 'Share Tech Mono, monospace',
    });
    this.scrollInner.add(shipIdText);

    yOffset += 80;

    // ---- Components section ----
    const compHeaderBg = this.scene.add.rectangle(10, yOffset, dw - 20, 26, SECTION_HEADER_COLOR, 1);
    compHeaderBg.setOrigin(0, 0);
    this.scrollInner.add(compHeaderBg);

    const compHeader = this.scene.add.text(20, yOffset + 13, 'COMPONENTS', {
      fontSize: `${getResponsiveFontSize(12, this.scene)}px`,
      color: '#44aaff',
      fontFamily: 'Orbitron, Arial',
      fontStyle: 'bold',
    });
    compHeader.setOrigin(0, 0.5);
    this.scrollInner.add(compHeader);
    yOffset += 32;

    if (components.length === 0) {
      const noComp = this.scene.add.text(25, yOffset, '[empty slots]', {
        fontSize: `${getResponsiveFontSize(12, this.scene)}px`,
        color: '#556677',
        fontFamily: 'Share Tech Mono, monospace',
      });
      this.scrollInner.add(noComp);
      yOffset += 20;
    } else {
      for (const comp of components) {
        const statsEntries = comp.stats ? Object.entries(comp.stats) : [];
        const statStr = statsEntries.map(([k, v]) => `${v} ${k}`).join(', ');
        const compLine = `* ${comp.name} Mk${comp.currentTier} (${comp.category})${statStr ? ' - ' + statStr : ''}`;
        const compText = this.scene.add.text(25, yOffset, compLine, {
          fontSize: `${getResponsiveFontSize(11, this.scene)}px`,
          color: '#aabbcc',
          fontFamily: 'Share Tech Mono, monospace',
          wordWrap: { width: dw - 60 },
        });
        this.scrollInner.add(compText);
        yOffset += compText.height + 4;
      }
    }

    yOffset += 8;

    // ---- Crew section ----
    const crewHeaderBg = this.scene.add.rectangle(10, yOffset, dw - 20, 26, SECTION_HEADER_COLOR, 1);
    crewHeaderBg.setOrigin(0, 0);
    this.scrollInner.add(crewHeaderBg);

    const crewHeader = this.scene.add.text(20, yOffset + 13, 'CREW', {
      fontSize: `${getResponsiveFontSize(12, this.scene)}px`,
      color: '#44aaff',
      fontFamily: 'Orbitron, Arial',
      fontStyle: 'bold',
    });
    crewHeader.setOrigin(0, 0.5);
    this.scrollInner.add(crewHeader);
    yOffset += 32;

    if (!crew) {
      const noCrew = this.scene.add.text(25, yOffset, 'No crew assigned', {
        fontSize: `${getResponsiveFontSize(12, this.scene)}px`,
        color: '#556677',
        fontFamily: 'Share Tech Mono, monospace',
      });
      this.scrollInner.add(noCrew);
      yOffset += 20;

      const assignBtn = this.createSmallButton(25, yOffset, 120, 24, 'ASSIGN CREW', () => {
        EventBus.emit('open-crew-management');
        this.close();
      });
      this.scrollInner.add(assignBtn);
    } else {
      const crewNameLine = `${crew.name}  (Lv.${crew.level})  -  ${crew.status}`;
      const crewNameText = this.scene.add.text(25, yOffset, crewNameLine, {
        fontSize: `${getResponsiveFontSize(13, this.scene)}px`,
        color: '#ccddff',
        fontFamily: 'Share Tech Mono, monospace',
        fontStyle: 'bold',
      });
      this.scrollInner.add(crewNameText);
      yOffset += 20;

      // Skill bars
      const barW = Math.min(200, dw * 0.4);
      const skills: { label: string; value: number; color: number }[] = [
        { label: 'Combat', value: crew.combat_skill, color: 0xff4444 },
        { label: 'Piloting', value: crew.piloting_skill, color: 0x44aaff },
        { label: 'Navigation', value: crew.navigation_skill, color: 0x44ff88 },
        { label: 'Engineering', value: crew.engineering_skill, color: 0xffaa44 },
        { label: 'Science', value: crew.science_skill, color: 0xcc66ff },
      ];

      for (const skill of skills) {
        const skillLabel = this.scene.add.text(30, yOffset, `${skill.label}:`, {
          fontSize: `${getResponsiveFontSize(11, this.scene)}px`,
          color: '#8899bb',
          fontFamily: 'Share Tech Mono, monospace',
        });
        this.scrollInner.add(skillLabel);

        const valText = this.scene.add.text(130, yOffset, `${skill.value}`, {
          fontSize: `${getResponsiveFontSize(11, this.scene)}px`,
          color: '#ccddee',
          fontFamily: 'Share Tech Mono, monospace',
        });
        this.scrollInner.add(valText);

        const barX = 160;
        const barH = 8;
        const barBg = this.scene.add.rectangle(barX, yOffset + 2, barW, barH, 0x222233);
        barBg.setOrigin(0, 0);
        this.scrollInner.add(barBg);

        const fillW = Math.max(1, (skill.value / 100) * barW);
        const barFill = this.scene.add.rectangle(barX, yOffset + 2, fillW, barH, skill.color);
        barFill.setOrigin(0, 0);
        this.scrollInner.add(barFill);

        yOffset += 16;
      }

      yOffset += 8;

      const unassignBtn = this.createSmallButton(25, yOffset, 110, 24, 'UNASSIGN', () => {
        this.handleUnassignCrew(ship.id);
      });
      this.scrollInner.add(unassignBtn);
    }

    this.scrollUpdate?.();
  }

  // ================================================================== Shared widgets

  private createSmallButton(
    x: number,
    y: number,
    w: number,
    h: number,
    label: string,
    onClick: () => void,
  ): Phaser.GameObjects.Container {
    const btn = this.scene.add.container(x, y);

    const bg = this.scene.add.rectangle(0, 0, w, h, ACCENT_COLOR, 0.15);
    bg.setOrigin(0, 0);
    bg.setStrokeStyle(1, ACCENT_COLOR, 0.6);
    btn.add(bg);

    const text = this.scene.add.text(w / 2, h / 2, label, {
      fontSize: `${getResponsiveFontSize(11, this.scene)}px`,
      color: '#44aaff',
      fontFamily: 'Share Tech Mono, monospace',
      fontStyle: 'bold',
    });
    text.setOrigin(0.5);
    btn.add(text);

    btn.setSize(w, h);
    btn.setInteractive(
      new Phaser.Geom.Rectangle(0, 0, w, h),
      Phaser.Geom.Rectangle.Contains,
    );
    btn.on('pointerover', () => {
      bg.setFillStyle(ACCENT_COLOR, 0.3);
      text.setColor('#ffffff');
    });
    btn.on('pointerout', () => {
      bg.setFillStyle(ACCENT_COLOR, 0.15);
      text.setColor('#44aaff');
    });
    btn.on('pointerdown', onClick);

    return btn;
  }
}
